import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { AuthorBasePath } from '../services/AuthorPlatformService';

export interface PerAuthorPostFormat {
  id: string;
  name?: string;
  description?: string;
  formatType?: string;
  [key: string]: unknown;
}

function fallbackFormatsFor(_platformId: string): PerAuthorPostFormat[] {
  return [
    {
      id: 'long_text',
      name: 'Long text',
      formatType: 'long_text',
      description: 'Generic text post fallback used only when the catalog format read fails.',
    },
  ];
}

function normalizeFormats(docs: PerAuthorPostFormat[], defaultFormatIds: string[] = []): PerAuthorPostFormat[] {
  const rank = new Map(defaultFormatIds.map((id, index) => [id, index]));
  return docs
    .filter((format) => Boolean(format.id) && (format as any).isActive !== false)
    .sort((a, b) => {
      const aRank = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bRank = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      const aName = String(a.name ?? a.id);
      const bName = String(b.name ?? b.id);
      return aName.localeCompare(bName);
    });
}

function arrayOfStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map((item) => item.trim())
    : [];
}

export function usePerAuthorPostFormats(
  projectId: string | null | undefined,
  authorId: string | null | undefined,
  platformId: string | null | undefined,
  basePath: AuthorBasePath = 'authors',
): { formats: PerAuthorPostFormat[]; loading: boolean; error: Error | null } {
  const [formats, setFormats] = useState<PerAuthorPostFormat[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId && authorId && platformId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId || !platformId) {
      setFormats([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const platformRef = doc(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
    );
    const ref = collection(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
      'formats',
    );
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      async (snap) => {
        const platformSnap = await getDoc(platformRef).catch(() => null);
        const authorDefaultFormatIds = arrayOfStrings(platformSnap?.data()?.defaultFormatIds);
        const authorFormats = normalizeFormats(
          snap.docs.map((doc) => ({ ...(doc.data() as PerAuthorPostFormat), id: doc.id })),
          authorDefaultFormatIds,
        );
        if (authorFormats.length > 0) {
          setFormats(authorFormats);
          setLoading(false);
          return;
        }

        try {
          const [systemPlatformSnap, systemSnap] = await Promise.all([
            getDoc(doc(db, 'system_platforms', platformId)).catch(() => null),
            getDocs(collection(db, 'system_platforms', platformId, 'formats')),
          ]);
          const systemDefaultFormatIds = arrayOfStrings(systemPlatformSnap?.data()?.defaultFormatIds);
          const systemFormats = normalizeFormats(
            systemSnap.docs.map((doc) => ({ ...(doc.data() as PerAuthorPostFormat), id: doc.id })),
            systemDefaultFormatIds,
          );
          setFormats(systemFormats.length > 0 ? systemFormats : fallbackFormatsFor(platformId));
        } catch {
          setFormats(fallbackFormatsFor(platformId));
        }
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setFormats(fallbackFormatsFor(platformId));
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, platformId, basePath]);

  return { formats, loading, error };
}
